import {
  IControl,
  UIControl,
  SizeConstraint,
  MeasureParams,
  DrawParams,
  PdfSize,
  PdfRect,
} from '../../types';
import { registry } from '../registry';
import {
  drawTextClipped,
  getExplicitSize,
  measureText,
  getHorizontalAlignment,
} from '../primitives';
import {
  PdfPen,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
  PdfFont,
  PdfPoint,
  PdfUnit,
} from '../../../../pdf/pdf';

function measureLineHeight(font: PdfFont, page: PageGen | undefined): number {
  return page?.measureString('Ag', font).height ?? 10;
}

function drawCursor(
  page: PageGen,
  font: PdfFont,
  text: string,
  textRect: PdfRect,
  pen: PdfPen
): void {
  const textWidth = text ? page.measureString(text, font).width : 0;
  const lineH = measureLineHeight(font, page);
  const cursorX = textRect.x + textWidth;
  const cursorY = textRect.y + (textRect.height + lineH) / 2; // bottom edge of text
  const charW = page.measureString('n', font).width;
  page.drawLine(pen, new PdfPoint(cursorX, cursorY), new PdfPoint(cursorX + charW, cursorY));
}

const BUTTON_FILL = PdfBrush.White;
const BUTTON_RADIUS = PdfUnit.FromMillimeters(1.5);

const DEFAULT_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
const MARGIN = 3;

export const LabelControl: IControl = {
  type: 'Label',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const textSize = measureText(text, params.font, params.page);

    const width = explicit.width || Math.min(textSize.width + MARGIN * 2, constraint.width);
    const height = explicit.height || Math.min(textSize.height + MARGIN, constraint.height);

    return new PdfSize(Math.max(width, 60), Math.max(height, 12));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const hAlign = getHorizontalAlignment(control);
    if (control.content) {
      let align = ETextAlign.CenterLeft;
      if (hAlign === 'Center') align = ETextAlign.Center;
      else if (hAlign === 'Right') align = ETextAlign.TopRight;
      drawTextClipped(params.page, control.content, params.font, rect, align);
    }
  },
};

registry.register(LabelControl);

export const TextBoxControl: IControl = {
  type: 'TextBox',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const lineH = measureLineHeight(params.font, params.page);
    const textWidth = text ? measureText(text, params.font, params.page).width : 0;

    const width =
      explicit.width || Math.min(Math.max(textWidth + MARGIN * 4, 80), constraint.width);
    const height = explicit.height || lineH + MARGIN * 2;

    return new PdfSize(Math.max(width, 80), Math.max(height, 14));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, rect);
    const textRect = new PdfRect(rect.x + MARGIN, rect.y, rect.width - MARGIN * 2, rect.height);
    if (control.content) {
      drawTextClipped(params.page, control.content, params.font, textRect, ETextAlign.CenterLeft);
    }
    drawCursor(params.page, params.font, control.content || '', textRect, DEFAULT_PEN);
  },
};

registry.register(TextBoxControl);

export const PasswordBoxControl: IControl = {
  type: 'PasswordBox',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const lineH = measureLineHeight(params.font, params.page);
    const masked = text.replace(/./g, '\u2022');
    const textWidth = masked ? measureText(masked, params.font, params.page).width : 0;

    const width =
      explicit.width || Math.min(Math.max(textWidth + MARGIN * 4, 80), constraint.width);
    const height = explicit.height || lineH + MARGIN * 2;

    return new PdfSize(Math.max(width, 80), Math.max(height, 14));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, rect);
    const textRect = new PdfRect(rect.x + MARGIN, rect.y, rect.width - MARGIN * 2, rect.height);
    const masked = control.content ? control.content.replace(/./g, '\u2022') : '';
    if (masked) {
      drawTextClipped(params.page, masked, params.font, textRect, ETextAlign.CenterLeft);
    }
    drawCursor(params.page, params.font, masked, textRect, DEFAULT_PEN);
  },
};

registry.register(PasswordBoxControl);

export const ButtonControl: IControl = {
  type: 'Button',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const textSize = measureText(text, params.font, params.page);

    const width = explicit.width || Math.min(textSize.width + MARGIN * 4, constraint.width);
    const height = explicit.height || Math.min(textSize.height + MARGIN * 2, constraint.height);

    return new PdfSize(Math.max(width, 50), Math.max(height, 16));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    params.page.drawRoundedRectangle(DEFAULT_PEN, BUTTON_FILL, rect, BUTTON_RADIUS);
    if (control.content) {
      drawTextClipped(params.page, control.content, params.font, rect, ETextAlign.Center);
    }
  },
};

registry.register(ButtonControl);

export const CheckBoxControl: IControl = {
  type: 'CheckBox',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const textSize = measureText(text, params.font, params.page);
    const checkSize = 10;

    const width =
      explicit.width || Math.min(textSize.width + checkSize + MARGIN * 2, constraint.width);
    const height = explicit.height || Math.max(checkSize, textSize.height + MARGIN);

    return new PdfSize(Math.max(width, 40), Math.max(height, 10));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const checkSize = Math.min(rect.height - 2, 10);
    const checkRect = new PdfRect(
      rect.x + 1,
      rect.y + (rect.height - checkSize) / 2,
      checkSize,
      checkSize
    );

    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, checkRect);

    if (control.attrs.IsChecked === 'true') {
      const p = 2;
      const p1 = new PdfPoint(checkRect.x + p, checkRect.y + checkRect.height * 0.55);
      const p2 = new PdfPoint(
        checkRect.x + checkRect.width * 0.38,
        checkRect.y + checkRect.height - p
      );
      const p3 = new PdfPoint(checkRect.x + checkRect.width - p, checkRect.y + p);
      params.page.drawLine(DEFAULT_PEN, p1, p2);
      params.page.drawLine(DEFAULT_PEN, p2, p3);
    }

    const textRect = new PdfRect(
      checkRect.x + checkRect.width + MARGIN,
      rect.y,
      rect.width - checkRect.width - MARGIN,
      rect.height
    );
    if (control.content) {
      drawTextClipped(params.page, control.content, params.font, textRect, ETextAlign.CenterLeft);
    }
  },
};

registry.register(CheckBoxControl);

export const RadioButtonControl: IControl = {
  type: 'RadioButton',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || '';
    const textSize = measureText(text, params.font, params.page);
    const radioSize = 10;

    const width =
      explicit.width || Math.min(textSize.width + radioSize + MARGIN * 2, constraint.width);
    const height = explicit.height || Math.max(radioSize, textSize.height + MARGIN);

    return new PdfSize(Math.max(width, 40), Math.max(height, 10));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const radioSize = Math.min(rect.height - 2, 10);
    const centerX = rect.x + radioSize / 2;
    const centerY = rect.y + rect.height / 2;
    const radius = radioSize / 2;

    params.page.drawEllipse(
      DEFAULT_PEN,
      PdfBrush.White,
      new PdfRect(centerX - radius, centerY - radius, radioSize, radioSize)
    );

    if (control.attrs.IsChecked === 'true') {
      const innerRadius = radius - 2;
      params.page.drawEllipse(
        null,
        PdfBrush.Black,
        new PdfRect(centerX - innerRadius, centerY - innerRadius, innerRadius * 2, innerRadius * 2)
      );
    }

    const textRect = new PdfRect(
      rect.x + radioSize + MARGIN,
      rect.y,
      rect.width - radioSize - MARGIN,
      rect.height
    );
    if (control.content) {
      drawTextClipped(params.page, control.content, params.font, textRect, ETextAlign.CenterLeft);
    }
  },
};

registry.register(RadioButtonControl);
